import { useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiAiAssist,
  apiAiChatApply,
} from '../api';
import type { AiProposedLeg } from '../api';
import { reportSaveError } from '../lib/saveStatus';
import {
  canImport,
  isDuplicateSkip,
  legKey,
  legRoute,
  legTimes,
  legToChanges,
  matchPassengers,
  type FlightMemberOption,
} from '../lib/aiFlightsImport';

interface ImportFlightsAIModalProps {
  /** Band whose AI credits are metered and whose show is written. */
  bandId: string | null;
  /** The show the confirmed segments are added to. */
  showId: string;
  /** Members of this show — the only people that can be ticked on a segment. */
  members: FlightMemberOption[];
  /** Capability-aware gate: false ⇒ no active control in this modal at all. */
  canWrite: boolean;
  onClose: () => void;
  /** Called after at least one segment was really written — reload the list. */
  onImported: () => void;
}

// Read a File as base64 (no data-URL prefix) — same path as the rider import.
function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result ?? '');
      const comma = raw.indexOf(',');
      resolve(comma === -1 ? raw : raw.slice(comma + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

const MODE_ICON: Record<string, string> = { flight: '✈️', train: '🚆', ferry: '⛴️' };

/**
 * LOT A2 — "Import from booking (AI)".
 *
 * The AI only PROPOSES segments (read-only, metered). NOTHING is written until
 * the user ticks a segment and presses Import: then one `POST /api/ai/chat/apply`
 * (`add_flight_leg`) is sent PER TICKED SEGMENT — the server re-validates and
 * INSERTs; a segment already on the show comes back as `skipped: 'duplicate'`
 * and is displayed as such, never merged, never overwritten.
 *
 * Rendered inline (not through a portal): `.modal-overlay` is `position: fixed`,
 * so it covers the viewport wherever it sits in the tree.
 */
export default function ImportFlightsAIModal({
  bandId,
  showId,
  members,
  canWrite,
  onClose,
  onImported,
}: ImportFlightsAIModalProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [proposed, setProposed] = useState<AiProposedLeg[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [memberIds, setMemberIds] = useState<Record<number, Set<string>>>({});
  const [personName, setPersonName] = useState<Record<number, string>>({});
  const [duplicates, setDuplicates] = useState<Set<number>>(new Set());
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<{ added: number; dup: number; failed: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const hasInput = text.trim().length > 0 || pdfBase64 !== null;
  const canExtract = canWrite && !extracting && hasInput;
  const canApplyNow = canWrite && !applying && canImport(checked);

  const errorText = (err: unknown): string => {
    const status = (err as { status?: number } | null)?.status;
    const message = err instanceof Error ? err.message : '';
    if (status === 402) return t('transport.aiOutOfCredits');
    if (status === 422) return `${t('transport.aiRejected')}: ${message}`;
    if (status === 502) return t('transport.aiProviderDown');
    return message || t('transport.aiImportFailed');
  };

  const runExtract = async () => {
    if (!bandId || !canExtract) return;
    setExtracting(true);
    setError(null);
    setProposed([]);
    setWarnings([]);
    setChecked(new Set());
    setDuplicates(new Set());
    setOutcome(null);
    try {
      const res = await apiAiAssist({
        task: 'import_flights',
        bandId,
        ...(pdfBase64 ? { pdfBase64 } : { text }),
      });
      const legs = res.proposedLegs ?? [];
      setProposed(legs);
      setWarnings((res.warnings ?? []).filter((w) => !!w));
      // NO segment is pre-ticked: an import is never a bulk surprise.
      setChecked(new Set());
      // People ARE pre-ticked when the document's printed name matches a member
      // (name de-duplication). An unknown printed name stays free text only —
      // the import never creates a team member.
      const preselected: Record<number, Set<string>> = {};
      const freeText: Record<number, string> = {};
      legs.forEach((leg, index) => {
        const match = matchPassengers(leg.passenger_names ?? [], members);
        preselected[index] = new Set(match.memberIds);
        if (match.unmatched.length > 0) freeText[index] = match.unmatched.join(', ');
      });
      setMemberIds(preselected);
      setPersonName(freeText);
    } catch (err) {
      setError(errorText(err));
      reportSaveError('ai', err);
    } finally {
      setExtracting(false);
    }
  };

  const toggleSegment = (index: number) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index); else next.add(index);
      return next;
    });
  };

  const toggleMember = (index: number, id: string) => {
    setMemberIds((prev) => {
      const current = new Set(prev[index] ?? []);
      if (current.has(id)) current.delete(id); else current.add(id);
      return { ...prev, [index]: current };
    });
  };

  const apply = async () => {
    if (!bandId || !canApplyNow) return;
    setApplying(true);
    setError(null);
    const dupKeys = new Set<number>();
    let added = 0;
    let dup = 0;
    let failed = 0;
    let firstFailure: string | null = null;
    const toImport = proposed
      .map((leg, index) => ({ leg, index }))
      .filter(({ index }) => checked.has(index));
    try {
      // ONE apply per ticked segment: each one is an INSERT the server
      // re-validates on its own. Never a batch that silently half-succeeds.
      for (const { leg, index } of toImport) {
        try {
          const answer = await apiAiChatApply({
            bandId,
            action: {
              action: 'add_flight_leg',
              showId,
              changes: legToChanges(leg, {
                memberIds: [...(memberIds[index] ?? new Set<string>())],
                personName: personName[index] ?? '',
              }),
              summary: `${leg.mode} ${leg.flight_no || ''} ${legRoute(leg)}`.trim(),
            },
          });
          if (isDuplicateSkip(answer)) {
            dup += 1;
            dupKeys.add(index);
          } else {
            added += 1;
          }
        } catch (err) {
          failed += 1;
          if (!firstFailure) firstFailure = errorText(err);
        }
      }
    } finally {
      setApplying(false);
      setDuplicates(dupKeys);
      setOutcome({ added, dup, failed });
    }
    if (firstFailure) setError(firstFailure);
    if (added > 0) onImported();
  };

  const onFile = async (file: File | null | undefined) => {
    if (!file) return;
    setError(null);
    if (!/\.pdf$/i.test(file.name) && file.type !== 'application/pdf') {
      setError(t('transport.aiImportFailed'));
      return;
    }
    try {
      setPdfBase64(await readFileAsBase64(file));
      setFileName(file.name);
      setText('');
    } catch (err) {
      setError(errorText(err));
    }
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    void onFile(e.dataTransfer?.files?.[0]);
  };

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t('transport.aiImportTitle')}
      data-ai-flights-modal
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal ai-import-modal">
        <div className="modal-header">
          <h3 className="modal-title">{t('transport.aiImportTitle')}</h3>
          <button type="button" className="modal-close" aria-label={t('transport.aiClose')} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body ai-import-body">
          <p className="ai-import-hint">{t('transport.aiImportHint')}</p>

          {!canWrite ? (
            <p className="ai-import-error" role="alert" data-ai-readonly>
              {t('transport.aiReadOnly')}
            </p>
          ) : (
            <>
              <div
                className={`ai-import-dropzone${dragOver ? ' is-over' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                data-ai-dropzone
              >
                {fileName ? (
                  <span data-ai-filename>{fileName}</span>
                ) : (
                  <span>
                    {t('transport.aiImportDropPdf')}{' '}
                    <button type="button" className="btn btn-small" onClick={() => fileInputRef.current?.click()}>
                      {t('transport.aiImportChooseFile')}
                    </button>
                  </span>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => void onFile(e.target.files?.[0])}
                />
              </div>

              <textarea
                className="form-input"
                rows={6}
                value={text}
                placeholder={t('transport.aiImportPlaceholder')}
                disabled={extracting || !!pdfBase64}
                onChange={(e) => setText(e.target.value)}
                data-ai-text
              />

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!canExtract}
                  onClick={() => void runExtract()}
                  data-ai-extract
                >
                  {extracting ? t('transport.aiImportExtracting') : t('transport.aiImportExtract')}
                </button>
              </div>
            </>
          )}

          {error ? (
            <p className="ai-import-error" role="alert" data-ai-error>{error}</p>
          ) : null}

          {warnings.length > 0 ? (
            <ul className="ai-import-warnings" data-ai-warnings>
              {warnings.slice(0, 6).map((w, i) => <li key={`${i}-${w}`}>{w}</li>)}
            </ul>
          ) : null}

          {proposed.length === 0 && !extracting && (hasInput || outcome) ? (
            <p className="ai-import-hint" role="status" data-ai-empty>
              {t('transport.aiNoSegments')}
            </p>
          ) : null}

          {proposed.length > 0 ? (
            <ul className="ai-import-legs" data-ai-legs>
              {proposed.map((leg, index) => {
                const key = legKey(leg, index);
                const isDup = duplicates.has(index);
                const selectedMembers = memberIds[index] ?? new Set<string>();
                return (
                  <li key={key} className="ai-import-leg" data-ai-leg data-dup={isDup ? '1' : '0'}>
                    <label className="ai-import-leg-head">
                      <input
                        type="checkbox"
                        checked={checked.has(index)}
                        disabled={!canWrite || applying}
                        onChange={() => toggleSegment(index)}
                        data-ai-leg-check
                      />
                      <span className="ai-import-badge">{MODE_ICON[leg.mode] || '✈️'} {t(`transport.${leg.mode}`)}</span>
                      <span data-ai-leg-date>{leg.flight_date || '—'}</span>
                      <strong>{leg.airline || '—'}</strong>
                      <span>{leg.flight_no || '—'}</span>
                      <span>{legRoute(leg)}</span>
                      <span className="text-dim">{legTimes(leg)}</span>
                      {leg.booking_ref ? <span className="text-dim">{t('transport.ref')}: {leg.booking_ref}</span> : null}
                      {isDup ? (
                        <span className="ai-import-duplicate" data-ai-duplicate>
                          {t('transport.aiAlreadyOnShow')}
                        </span>
                      ) : null}
                    </label>

                    <div className="ai-import-leg-people">
                      <span className="text-dim">{t('transport.aiPassengers')}:</span>
                      {members.length === 0 ? (
                        <span className="text-dim">{t('transport.noMembers')}</span>
                      ) : members.map((m) => (
                        <label key={String(m.id)}>
                          <input
                            type="checkbox"
                            checked={selectedMembers.has(String(m.id))}
                            disabled={!canWrite || applying}
                            onChange={() => toggleMember(index, String(m.id))}
                          />
                          {m.name}
                        </label>
                      ))}
                      <input
                        className="form-input"
                        style={{ flex: '1 1 160px', minWidth: 120 }}
                        value={personName[index] ?? ''}
                        placeholder={t('transport.aiPersonName')}
                        disabled={!canWrite || applying || selectedMembers.size > 0}
                        onChange={(e) => setPersonName((prev) => ({ ...prev, [index]: e.target.value }))}
                        data-ai-person-name
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : null}

          {proposed.length > 0 ? (
            <p className="ai-import-hint" role="status" data-ai-not-selected>
              {checked.size === 0 ? t('transport.aiNothingSelected') : null}
            </p>
          ) : null}

          {outcome ? (
            <p className="ai-import-hint" role="status" data-ai-outcome>
              {outcome.added > 0 ? t('transport.aiImportDone', { count: outcome.added }) : null}
              {outcome.added > 0 && outcome.dup > 0 ? ' ' : null}
              {outcome.dup > 0 ? t('transport.aiImportDuplicates', { count: outcome.dup }) : null}
              {(outcome.added > 0 || outcome.dup > 0) && outcome.failed > 0 ? ' ' : null}
              {outcome.failed > 0 ? t('transport.aiImportFailedCount', { count: outcome.failed }) : null}
              {outcome.added === 0 && outcome.dup === 0 && outcome.failed === 0 ? t('transport.aiNothingSelected') : null}
            </p>
          ) : null}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('transport.aiClose')}
          </button>
          {canWrite ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={!canApplyNow}
              onClick={() => void apply()}
              data-ai-apply
            >
              {applying ? t('transport.aiImportApplying') : t('transport.aiImportApply', { count: checked.size })}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
